"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractHandshake = exports.MIN_NONCE = void 0;
const buffer_1 = require("buffer");
const aead_1 = __importDefault(require("bcrypto/lib/js/aead"));
const x25519_1 = __importDefault(require("bcrypto/lib/js/x25519"));
const sha256_1 = __importDefault(require("bcrypto/lib/js/sha256"));
const utils_1 = require("../utils");
const logger_1 = require("../logger");
exports.MIN_NONCE = 0;
class AbstractHandshake {
    encryptWithAd(cs, ad, plaintext) {
        const e = this.encrypt(cs.k, cs.n, ad, plaintext);
        this.setNonce(cs, this.incrementNonce(cs.n));
        return e;
    }
    decryptWithAd(cs, ad, ciphertext) {
        const { plaintext, valid } = this.decrypt(cs.k, cs.n, ad, ciphertext);
        this.setNonce(cs, this.incrementNonce(cs.n));
        return { plaintext, valid };
    }
    // Cipher state related
    hasKey(cs) {
        return !this.isEmptyKey(cs.k);
    }
    setNonce(cs, nonce) {
        cs.n = nonce;
    }
    createEmptyKey() {
        return buffer_1.Buffer.alloc(32);
    }
    isEmptyKey(k) {
        const emptyKey = this.createEmptyKey();
        return emptyKey.equals(k);
    }
    incrementNonce(n) {
        return n + 1;
    }
    nonceToBytes(n) {
        const nonce = buffer_1.Buffer.alloc(12);
        nonce.writeUInt32LE(n, 4);
        return nonce;
    }
    encrypt(k, n, ad, plaintext) {
        const nonce = this.nonceToBytes(n);
        const ctx = new aead_1.default();
        plaintext = buffer_1.Buffer.from(plaintext);
        ctx.init(k, nonce);
        ctx.aad(ad);
        ctx.encrypt(plaintext);
        // Encryption is done on the sent reference
        return buffer_1.Buffer.concat([plaintext, ctx.final()]);
    }
    encryptAndHash(ss, plaintext) {
        let ciphertext;
        if (this.hasKey(ss.cs)) {
            ciphertext = this.encryptWithAd(ss.cs, ss.h, plaintext);
        }
        else {
            ciphertext = plaintext;
        }
        this.mixHash(ss, ciphertext);
        return ciphertext;
    }
    decrypt(k, n, ad, ciphertext) {
        const nonce = this.nonceToBytes(n);
        const ctx = new aead_1.default();
        ciphertext = buffer_1.Buffer.from(ciphertext);
        const tag = ciphertext.slice(ciphertext.length - 16);
        ciphertext = ciphertext.slice(0, ciphertext.length - 16);
        ctx.init(k, nonce);
        ctx.aad(ad);
        ctx.decrypt(ciphertext);
        // Decryption is done on the sent reference
        return { plaintext: ciphertext, valid: ctx.verify(tag) };
    }
    decryptAndHash(ss, ciphertext) {
        let plaintext;
        let valid = true;
        if (this.hasKey(ss.cs)) {
            ({ plaintext, valid } = this.decryptWithAd(ss.cs, ss.h, ciphertext));
        }
        else {
            plaintext = ciphertext;
        }
        this.mixHash(ss, ciphertext);
        return { plaintext, valid };
    }
    dh(privateKey, publicKey) {
        try {
            const derived = x25519_1.default.derive(publicKey, privateKey);
            const result = buffer_1.Buffer.alloc(32);
            derived.copy(result);
            return result;
        }
        catch (e) {
            logger_1.logger(e.message);
            return buffer_1.Buffer.alloc(32);
        }
    }
    mixHash(ss, data) {
        ss.h = this.getHash(ss.h, data);
    }
    getHash(a, b) {
        return sha256_1.default.digest(buffer_1.Buffer.from([...a, ...b]));
    }
    mixKey(ss, ikm) {
        const [ck, tempK] = utils_1.getHkdf(ss.ck, ikm);
        ss.cs = this.initializeKey(tempK);
        ss.ck = ck;
    }
    initializeKey(k) {
        const n = exports.MIN_NONCE;
        return { k, n };
    }
    // Symmetric state related
    initializeSymmetric(protocolName) {
        const protocolNameBytes = buffer_1.Buffer.from(protocolName, 'utf-8');
        const h = this.hashProtocolName(protocolNameBytes);
        const ck = h;
        const key = this.createEmptyKey();
        const cs = this.initializeKey(key);
        return { cs, ck, h };
    }
    hashProtocolName(protocolName) {
        if (protocolName.length <= 32) {
            const h = buffer_1.Buffer.alloc(32);
            protocolName.copy(h);
            return h;
        }
        else {
            return this.getHash(protocolName, buffer_1.Buffer.alloc(0));
        }
    }
    split(ss) {
        const [tempk1, tempk2] = utils_1.getHkdf(ss.ck, buffer_1.Buffer.alloc(0));
        const cs1 = this.initializeKey(tempk1);
        const cs2 = this.initializeKey(tempk2);
        return { cs1, cs2 };
    }
    writeMessageRegular(cs, payload) {
        const ciphertext = this.encryptWithAd(cs, buffer_1.Buffer.alloc(0), payload);
        const ne = this.createEmptyKey();
        const ns = buffer_1.Buffer.alloc(0);
        return { ne, ns, ciphertext };
    }
    readMessageRegular(cs, message) {
        return this.decryptWithAd(cs, buffer_1.Buffer.alloc(0), message.ciphertext);
    }
}
exports.AbstractHandshake = AbstractHandshake;
//# sourceMappingURL=abstract-handshake.js.map